import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import {
  ArrowNavigation,
  MultiSelect,
  VirtualizedAutocomplete
} from 'components/shared';
import { optionPadding } from 'components/shared/Autocomplete/MuiAutocompleteOption';
import { defaultListWidth } from './Autocomplete/VirtualizedAutocomplete.consts';

import type { FieldState } from 'components/flex-portal/FlexMatch/components/form/PreferencesSection.types';
import type { AutocompleteRenderInputParams } from '@mui/material';

const EmployeeSelectorStyles = styled.div`
  display: flex;
  width: 100%;

  .controls {
    margin-left: 5px;
  }

  .MuiAutocomplete-root {
    width: 100%;
  }
`;

export type Employee = {
  id: number;
  employeeId: number;
  employeeName?: string;
  nr: number;
  employeeNr?: number;
  label: string;
};

export type EmployeeSelectorHandle = {
  uncheckEmployee: (
    employee: Employee,
    shouldTriggerChangeEvent?: boolean
  ) => void;
  checkEmployee: (
    employee: Employee,
    shouldTriggerChangeEvent?: boolean
  ) => void;
  setSelectedEmployees: (employees: Employee[]) => void;
};

type Props = {
  options: Employee[];
  showNavigation?: boolean;
  disablePortal?: true;
  /**
   * The employee to show as selected. A new instance is treated as the parent handing over
   * a selection: it is adopted, and reported through `onSelected` only when its `id` differs
   * from the employee currently selected. Re-passing the same instance is inert, so a
   * selection the user made inside the selector (dropdown, arrows) is never overridden by a
   * `selectedValue` the parent merely kept passing along.
   */
  selectedValue?: Employee;
  disabled?: boolean;
  variant?: 'standard' | 'outlined';
  fieldState?: FieldState;
  fullWidth?: boolean;
  /** puts the cursor in the field on mount, for a dialog that opens on it */
  autoFocus?: boolean;
} & (
  | {
      isMulti: true;
      onSelected: (employees: Employee[]) => void;
    }
  | {
      isMulti?: never;
      onSelected: (employee: Employee) => void;
    }
);

export default forwardRef(function EmployeeSelector(
  {
    options = [],
    showNavigation,
    disablePortal,
    isMulti,
    selectedValue,
    onSelected,
    disabled,
    variant = 'standard',
    fieldState,
    fullWidth = false,
    autoFocus
  }: Props,
  ref
) {
  'use memo';

  const { t } = useTranslation('common');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
  // The last selectedValue instance that was applied. Guarding on the prop instance -- not on
  // the internal selection -- is what keeps the mirror effect from undoing a selection the
  // user made in the selector itself (#2575).
  const [lastSelectedValue, setLastSelectedValue] = useState<
    Employee | undefined
  >(undefined);

  const onArrowNavigate = (
    direction: 'prev' | 'next',
    currentIndex: number
  ) => {
    let nextEmployee: Employee;
    if (direction === 'prev') {
      nextEmployee = options[currentIndex - 1];
    } else {
      nextEmployee = options[selectedEmployee ? currentIndex + 1 : 0];
    }

    if (isMulti === true) {
      setSelectedEmployees([nextEmployee]);
      onSelected([nextEmployee]);
    } else {
      setSelectedEmployee(nextEmployee);
      onSelected(nextEmployee);
    }
  };

  const renderInput = (params: AutocompleteRenderInputParams) => {
    const { value } = params.inputProps;
    const valueString = String(value);

    if (/\d+\s-\s/.test(valueString)) {
      params.InputProps.startAdornment = (
        <InputAdornment position="start">
          <small>
            <i>{valueString.match(/\d+/)?.[0]}</i>
          </small>
          &nbsp; -
        </InputAdornment>
      );
      params.inputProps.value = valueString.replace(/\d+\s-\s/, '');
    }
    return (
      <TextField
        {...params}
        variant={variant}
        size="small"
        label={t('employeeName')}
        fullWidth={fullWidth}
        autoFocus={autoFocus}
        sx={{
          width: fullWidth ? '100%' : defaultListWidth
        }}
        {...(fieldState
          ? { error: !!fieldState.error, helperText: fieldState.error?.message }
          : {})}
      />
    );
  };

  useEffect(() => {
    if (isMulti) {
      if (selectedEmployees.length) {
        const filteredEmployees = selectedEmployees.filter((selectedEmployee) =>
          options.find((o) => o.id === selectedEmployee.id)
        );
        if (filteredEmployees.length !== selectedEmployees.length) {
          setSelectedEmployees(filteredEmployees);
          onSelected(filteredEmployees);
        }
      }
    } else if (selectedValue && selectedValue !== lastSelectedValue) {
      // A new selectedValue instance is the parent handing over a selection; the same
      // instance re-passed on a re-render is not. That distinction is load-bearing: the
      // dojo EmployeeCardWidget keeps passing the previous selectedValue while the user
      // navigates within the selector, and comparing against the internal selection here
      // made this effect snap the selection back and re-report the old employee, with
      // interleaved detail loads (and mismatched empId/waId requests) as a result (#2575).
      setLastSelectedValue(selectedValue);
      setSelectedEmployee(selectedValue);
      if (selectedValue.id !== selectedEmployee?.id) {
        // Only report an employee the parent does not already know about. A refreshed
        // instance under the same id is adopted silently -- re-notifying it is what caused
        // the redundant `_getEmployeeDetails` calls of #1652.
        onSelected(selectedValue);
      }
    }
    // `onSelected` stays a reactive dependency rather than going through `useLatestRef` like
    // the other call sites: it is a discriminated union with `isMulti`, and TypeScript can
    // only narrow it through the destructured binding, not through a ref. Re-running this
    // effect is harmless -- the guards above compare against the applied instance, so
    // nothing is reported twice.
  }, [
    options,
    isMulti,
    onSelected,
    lastSelectedValue,
    selectedEmployee?.id,
    selectedEmployees,
    selectedValue
  ]);

  useImperativeHandle(ref, () => ({
    uncheckEmployee(employee, shouldTriggerChangeEvent = true) {
      const newSelection = selectedEmployees.filter(
        ({ employeeId }) => employeeId !== employee.employeeId
      );
      setSelectedEmployees(newSelection);
      if (shouldTriggerChangeEvent && isMulti) {
        onSelected(newSelection);
      }
    },
    checkEmployee(employee, shouldTriggerChangeEvent = false) {
      const newSelection = [...selectedEmployees, employee];
      setSelectedEmployees(newSelection);
      if (shouldTriggerChangeEvent && isMulti) {
        onSelected(newSelection);
      }
    },
    setSelectedEmployees(employees) {
      setSelectedEmployees(employees);
    }
  }));

  return (
    <EmployeeSelectorStyles className="employee-selector">
      {isMulti ? (
        <MultiSelect
          optionLabelProp="label"
          renderInput={renderInput}
          options={options}
          selectedOptions={selectedEmployees}
          onChange={(options) => {
            setSelectedEmployees(options);
            onSelected(options);
          }}
          disablePortal={disablePortal}
          disabled={disabled}
        />
      ) : (
        <VirtualizedAutocomplete
          value={selectedEmployee}
          options={options}
          optionLabelProp="label"
          getOptionLabel={(o) => {
            if (typeof o === 'string') {
              return o;
            }

            return o?.label || '';
          }}
          renderInput={renderInput}
          onChange={(e, value: any) => {
            setSelectedEmployee(value || null);
            onSelected(value || null);
          }}
          autoHighlight
          extraWidth={optionPadding}
          disablePortal={disablePortal}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          disabled={disabled}
        />
      )}
      {showNavigation && !isMulti && (
        <ArrowNavigation
          selectedOption={selectedEmployee}
          options={options}
          showCnt
          onNavigate={onArrowNavigate}
        />
      )}
    </EmployeeSelectorStyles>
  );
});
